import { useRouter } from "next/router"
import {
  type ComponentType,
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react"
import { getHashPath } from "@/components/resources/utils/routerHelpers"

/*
 * ScrollPage exits because of need to update router path and/or hash via different components based on scroll
 * position. Components using it can be eg. sidebar navigation (in layout) and table of contents (in template if rendered) or
 * the alongside provided ScrollPageSection (in route atm.).
 *
 * At the very least it just provides a ref prop "isScrolling" (short for is programmatically scrolling) to allow different
 * components not causing overlapping effects and "offset" to keep things consistent between initial scroll pos. and client side rendered.
 */

type LooseScrollOffset = number | { page: number; anchor: number } // Plain offset or offset per page/path anc anchor/hash
type ScrollOffset = Exclude<LooseScrollOffset, number> // Plain offset or offset per page/path anc anchor/hash

type ScrollStatus = {
  isProgrammaticallyScrolling: boolean
  isScrollingUp: boolean // Helps ToC and better to keep track of it in one place
  offset: ScrollOffset
}

interface ScrollCtx {
  scrollStatus: ScrollStatus
  scrollNavigate: () => void
}

const ScrollContext = createContext<ScrollCtx>({
  scrollNavigate: () => {},
  scrollStatus: {
    isProgrammaticallyScrolling: false,
    isScrollingUp: false,
    offset: { page: 0, anchor: 0 },
  },
})

export const useScrollContext = () => {
  const context = useContext(ScrollContext)

  return context
}

const resolveOffset = (offset: LooseScrollOffset): ScrollOffset =>
  typeof offset === "number" ? { page: offset, anchor: offset } : offset

export const ScrollPageProvider = ({
  children,
  scrollOffset,
}: {
  children: ReactNode
  scrollOffset: LooseScrollOffset
}) => {
  const { asPath } = useRouter()
  const initialPath = useRef(getHashPath(asPath)) // Initial path where we use the hash too
  const currentPageSlug = useMemo(() => getHashPath(asPath).path.split("/").slice(-1)[0], [asPath])

  const scrollRef = useRef<ScrollStatus>({
    isProgrammaticallyScrolling: true, // This component/provider triggers scrolling unless stated otherwise
    offset: resolveOffset(scrollOffset),
    isScrollingUp: false,
  })
  const scrolledOnceRef = useRef(false)
  const prevScrollRef = useRef(0)
  const isScrolling = useRef(false)

  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout | undefined

    const onScrollEnd = () => {
      scrollRef.current.isProgrammaticallyScrolling = false
      isScrolling.current = false
    }

    const onScroll = () => {
      isScrolling.current = true
      scrollRef.current.isScrollingUp = prevScrollRef.current > window.scrollY
      prevScrollRef.current = window.scrollY
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(onScrollEnd, 150)

      if (!scrolledOnceRef.current) {
        scrolledOnceRef.current = true
      }
    }

    document.addEventListener("scroll", onScroll)

    return () => {
      document.removeEventListener("scroll", onScroll)

      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }
    }
  }, [])

  useEffect(() => {
    // To be on the safe side, check scroll dir. Useful for example on page change after scrolling up on previous page, because the component doesn't unmount
    scrollRef.current.isScrollingUp = prevScrollRef.current > window.scrollY

    if (isScrolling.current && !scrollRef.current.isProgrammaticallyScrolling) {
      // Dont do any scrolling if user is already scrolling naturally. But allow overriding our own scrolls
      return undefined
    }

    let hash: null | string = null
    let pageSlug: string
    let scrollToTimeout: number | undefined
    if (!scrolledOnceRef.current) {
      hash = initialPath.current.hash
      pageSlug = initialPath.current.path.split("/").slice(-1)[0]
    } else {
      pageSlug = currentPageSlug
    }

    /*
     * NOTE: this wont work in dev mode when used via layout without the children as deps. The reason is that data is not static
     * and layout wont be re-rendered when data finally comes.
     *
     * - Also this component doesn't unmount on route updates
     * - Also this component doesnt handle anchor scrolling (hash scrolling) after initialisation. Eg. TableOfContents handles that. Maybe it should
     */
    const elementToScrollTo = hash
      ? document.querySelector(`[data-anchor="${hash}"]`)
      : document.querySelector(`[data-page="${pageSlug}"]`)

    if (elementToScrollTo) {
      scrollToTimeout = requestAnimationFrame(() => {
        // Double check because of raf
        if (!isScrolling.current || scrollRef.current.isProgrammaticallyScrolling) {
          window.scrollBy({
            top:
              elementToScrollTo.getBoundingClientRect().top -
              scrollRef.current.offset[hash ? "anchor" : "page"],
            behavior:
              scrollRef.current.isProgrammaticallyScrolling && scrolledOnceRef.current
                ? "smooth"
                : "auto",
          })
          prevScrollRef.current = window.scrollY

          if (!scrolledOnceRef.current) {
            scrolledOnceRef.current = true
          }
        }
      })
    } else {
      scrollRef.current.isProgrammaticallyScrolling = false
    }

    return () => {
      if (scrollToTimeout) {
        clearTimeout(scrollToTimeout)
      }
    }
  }, [currentPageSlug])

  const scrollNavigate = useCallback(() => {
    /*
     * Simple function that should be called when ever triggering navigation that happens via
     * scrolling instead of loading a new document from server side.
     * This allows for example TableOfContents to disable its overlapping actions during the
     * programmatical scrolling. The main reason for this provider
     *
     */
    scrollRef.current.isProgrammaticallyScrolling = true
  }, [])

  return (
    <ScrollContext.Provider
      value={{
        scrollNavigate,
        scrollStatus: scrollRef.current,
      }}
    >
      {children}
    </ScrollContext.Provider>
  )
}

/**
 * Lightweight scroll offset provider for pages that need TOC scroll offset
 * but don't need the programmatic scroll-to-element behavior of ScrollPageProvider.
 * Use this for single-article pages (e.g., engineering blog) where ScrollPageProvider's
 * initial scroll logic causes intersection observer conflicts.
 */
export const ScrollOffsetProvider = ({
  children,
  scrollOffset,
}: {
  children: ReactNode
  scrollOffset: LooseScrollOffset
}) => {
  const scrollRef = useRef<ScrollStatus>({
    isProgrammaticallyScrolling: false,
    isScrollingUp: false,
    offset: resolveOffset(scrollOffset),
  })
  const prevScrollRef = useRef(0)

  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout | undefined

    const onScrollEnd = () => {
      scrollRef.current.isProgrammaticallyScrolling = false
    }

    const onScroll = () => {
      scrollRef.current.isScrollingUp = prevScrollRef.current > window.scrollY
      prevScrollRef.current = window.scrollY
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(onScrollEnd, 150)
    }

    document.addEventListener("scroll", onScroll)

    return () => {
      document.removeEventListener("scroll", onScroll)

      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }
    }
  }, [])

  const scrollNavigate = useCallback(() => {
    scrollRef.current.isProgrammaticallyScrolling = true
  }, [])

  return (
    <ScrollContext.Provider
      value={{
        scrollNavigate,
        scrollStatus: scrollRef.current,
      }}
    >
      {children}
    </ScrollContext.Provider>
  )
}

export const withScrollContext = <T extends Record<any, unknown>>(
  Component: ComponentType<T>,
  { scrollOffset }: { scrollOffset: LooseScrollOffset } = { scrollOffset: 0 },
) => {
  const ScrollPage = (props: T) => (
    <ScrollPageProvider scrollOffset={scrollOffset}>
      <Component {...props} />
    </ScrollPageProvider>
  )

  ScrollPage.displayName = "ScrollPage"

  return ScrollPage
}
